<template>
    <div class="wan-model-page">
        <!-- Video Generator Section -->
        <div ref="generatorSection" class="video-generator-section">
            <div class="container">
                <div class="generator-container">
                    <!-- Generator Header -->
                    <div class="generator-header">
                        <div class="generator-title">
                            <h1>{{ t('wan-2-6.generatorTitle') }}</h1>
                            <p class="generator-description">
                                {{ t('wan-2-6.generatorDescription')
                                }}<span class="highlight-red">{{
                                    t('wan-2-6.generatorDescriptionHighlight1')
                                    }}</span>{{ t('wan-2-6.generatorDescriptionText2')
                                    }}<span class="highlight-red">{{
                                    t('wan-2-6.generatorDescriptionHighlight2')
                                    }}</span>{{ t('wan-2-6.generatorDescriptionEnd') }}
                            </p>
                            <div class="rainbow-bar"></div>
                        </div>
                    </div>

                    <!-- Generator Interface -->
                    <VideoGenerator model-name="Wan" :tabs="tabs" redirect-url="/ai-video" @generate="handleGenerate"
                        model-key="WAN_2_6">
                        <template #model-logo>
                            <div class="wan-logo">W</div>
                        </template>
                    </VideoGenerator>
                </div>
            </div>
        </div>

        <!-- Core Features Section -->
        <div class="core-features-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('wan-2-6.coreFeaturesTitle') }}
                    </h2>
                </div>

                <div class="features-showcase-container">
                    <!-- Native Audio Integration Feature -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{
                                        t('wan-2-6.nativeAudioIntegrationTitle')
                                    }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'wan-2-6.nativeAudioIntegrationDescription'
                                        )
                                    }}
                                </p>
                            </div>
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.nativeAudioIntegration"
                                    :poster="
                                        getResourceThumbnail(
                                            demoVideos.nativeAudioIntegration
                                        )
                                    "
                                    :title="
                                        t('wan-2-6.nativeAudioIntegrationTitle')
                                    "
                                    :description="
                                        t(
                                            'wan-2-6.nativeAudioIntegrationDescription'
                                        )
                                    "
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Enhanced Dynamic Effects Feature -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.enhancedDynamicEffects"
                                    :poster="
                                        getResourceThumbnail(
                                            demoVideos.enhancedDynamicEffects
                                        )
                                    "
                                    :title="
                                        t(
                                            'wan-2-6.enhancedDynamicEffectsTitle'
                                        )
                                    "
                                    :description="
                                        t(
                                            'wan-2-6.enhancedDynamicEffectsDescription'
                                        )
                                    "
                                />
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{
                                        t('wan-2-6.enhancedDynamicEffectsTitle')
                                    }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'wan-2-6.enhancedDynamicEffectsDescription'
                                        )
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>

                    <!-- Precise Camera Control Feature -->
                    <div class="feature-showcase">
                        <div class="feature-content">
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{ t('wan-2-6.preciseCameraControlTitle') }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'wan-2-6.preciseCameraControlDescription'
                                        )
                                    }}
                                </p>
                            </div>
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.preciseCameraControl"
                                    :poster="
                                        getResourceThumbnail(
                                            demoVideos.preciseCameraControl
                                        )
                                    "
                                    :title="
                                        t('wan-2-6.preciseCameraControlTitle')
                                    "
                                    :description="
                                        t(
                                            'wan-2-6.preciseCameraControlDescription'
                                        )
                                    "
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Enhanced Visual Realism Feature -->
                    <div class="feature-showcase reverse">
                        <div class="feature-content">
                            <div class="feature-video">
                                <VideoPlayer
                                    :src="demoVideos.enhancedVisualRealism"
                                    :poster="
                                        getResourceThumbnail(
                                            demoVideos.enhancedVisualRealism
                                        )
                                    "
                                    :title="
                                        t(
                                            'wan-2-6.enhancedVisualRealismTitle'
                                        )
                                    "
                                    :description="
                                        t(
                                            'wan-2-6.enhancedVisualRealismDescription'
                                        )
                                    "
                                />
                            </div>
                            <div class="feature-text">
                                <h3 class="feature-title">
                                    {{
                                        t('wan-2-6.enhancedVisualRealismTitle')
                                    }}
                                </h3>
                                <p class="feature-description">
                                    {{
                                        t(
                                            'wan-2-6.enhancedVisualRealismDescription'
                                        )
                                    }}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- How to Use Section -->
        <div class="how-to-use-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('wan-2-6.howToUseTitle') }}
                    </h2>
                </div>

                <div class="steps-container">
                    <div class="step-item">
                        <div class="step-number">1</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('wan-2-6.step1Title') }}
                            </h3>
                            <p class="step-description">
                                {{ t('wan-2-6.step1Description') }}
                            </p>
                        </div>
                    </div>

                    <div class="step-item">
                        <div class="step-number">2</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('wan-2-6.step2Title') }}
                            </h3>
                            <p class="step-description">
                                {{ t('wan-2-6.step2Description') }}
                            </p>
                        </div>
                    </div>

                    <div class="step-item">
                        <div class="step-number">3</div>
                        <div class="step-content">
                            <h3 class="step-title">
                                {{ t('wan-2-6.step3Title') }}
                            </h3>
                            <p class="step-description">
                                {{ t('wan-2-6.step3Description') }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- YouTube Videos Section -->
        <div class="youtube-videos-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('wan-2-6.youtubeTitle') }}
                    </h2>
                </div>

                <div class="youtube-carousel">
                    <button class="carousel-nav-btn carousel-nav-left" @click="previousVideo"
                        :aria-label="t('common.previous')">
                        <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd"
                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                clip-rule="evenodd" />
                        </svg>
                    </button>

                    <div class="videos-wrapper">
                        <div class="videos-scroll" :style="{
                            transform: `translateX(-${currentVideoIndex * (100 / 3)}%)`,
                        }">
                            <div v-for="(video, index) in youtubeVideos" :key="index" class="video-iframe-container">
                                <iframe :src="video.embedUrl" :title="video.title" class="video-iframe" frameborder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowfullscreen></iframe>
                            </div>
                        </div>
                    </div>

                    <button class="carousel-nav-btn carousel-nav-right" @click="nextVideo"
                        :aria-label="t('common.next')">
                        <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd"
                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                clip-rule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <!-- X Posts Section -->
        <TwitterPosts :title="t('wan-2-6.xPostsTitle')" :tweet-urls="twitterTweetUrls" />

        <!-- Explore Other Models Section -->
        <div class="explore-models-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">
                        {{ t('wan-2-6.exploreModelsTitle') }}
                    </h2>
                </div>

                <div class="models-grid">
                    <NuxtLink to="/video/runway-ai-4-5" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/a65ca4242d4a0dc3f1b65e1d96471144.png"
                                :alt="t('wan-2-6.runwayTitle')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('wan-2-6.runwayTitle') }}
                        </h3>
                    </NuxtLink>
                    <NuxtLink to="/video/kling-2.6-pro" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/1fda745d93db271f6da3cedc576b515a.png"
                                :alt="t('wan-2-6.kling_2_6')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('wan-2-6.kling_2_6') }}
                        </h3>
                    </NuxtLink>
                    <NuxtLink to="/video/veo/veo-3-1" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/3ed8c9e1a1ec3b37133c608c292821cb.png"
                                :alt="t('wan-2-6.veo31Title')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('wan-2-6.veo31Title') }}
                        </h3>
                    </NuxtLink>
                    <NuxtLink to="/video/sora/sora-2" class="model-card">
                        <div class="model-icon">
                            <img src="https://static.weryai.com/growth/image/7ac1a1457b1e788829a2da1d178879bb.png"
                                :alt="t('wan-2-6.sora2Title')" />
                        </div>
                        <h3 class="model-card-title">
                            {{ t('wan-2-6.sora2Title') }}
                        </h3>
                    </NuxtLink>
                </div>
            </div>
        </div>

        <!-- FAQ Section -->
        <div class="faq-section">
            <div class="container">
                <div class="section-header">
                    <h2 class="section-title">{{ t('wan-2-6.faqTitle') }}</h2>
                </div>

                <div class="faq-list">
                    <div v-for="(faq, index) in faqData" :key="index" class="faq-item"
                        :class="{ active: activeFaqIndex === index }">
                        <div class="faq-question" @click="toggleFaq(index)">
                            <!-- <span class="faq-icon">></span> -->
                            <span class="faq-text">{{ faq.question }}</span>
                        </div>
                        <div class="faq-answer">
                            <p>{{ faq.answer }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- CTA Section -->
        <div class="cta-section">
            <div class="container">
                <div class="cta-content">
                    <!-- 微鲤AI Logo -->
                    <div class="cta-logo">
                        <div class="logo-icon logo-image">
                            <!-- <img src="/weryai_48.png" alt="微鲤AI" class="logo-image" /> -->
                            <img src="https://static.weryai.com/growth/image/4608e0a35205ff929dba2ea3c51f6e62.webp"
                                alt="微鲤AI logo" class="logo-image" />
                        </div>
                    </div>

                    <!-- Main Content -->
                    <div class="cta-header">
                        <h2 class="cta-title">{{ t('wan-2-6.ctaTitle') }}</h2>
                        <p class="cta-description">
                            {{ t('wan-2-6.ctaDescription') }}
                        </p>
                    </div>

                    <!-- CTA Button -->
                    <div class="cta-actions">
                        <button @click="scrollToGenerator" class="cta-primary-btn">
                            {{ t('wan-2-6.ctaButton') }}
                        </button>

                        <p class="cta-disclaimer">
                            {{ t('wan-2-6.ctaDisclaimer') }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useHead, useRequestURL } from '#imports'
import { useI18n } from '~/composables/useI18n'
import { useHowToSeo } from '~/composables/useHowToSeo'
import { useVideoSeo } from '~/composables/useVideoSeo'
import { useMultilingualSeo } from '~/composables/useMultilingualSeo'
import { analyticsEvent } from '~/utils/log-event-func'
import { getResourceThumbnail } from '~/utils/common'

const { t } = useI18n()
const requestUrl = useRequestURL()
const requestUrlHref = requestUrl.href

const demoVideos = {
    nativeAudioIntegration:
        'https://static.weryai.com/growth/video/weryai_333deb3487c1444863b95ecf3fb17598.mp4',
    enhancedDynamicEffects:
        'https://static.weryai.com/growth/video/weryai_9fdf8c67fd063fef884bc89932728122.mp4',
    preciseCameraControl:
        'https://static.weryai.com/growth/video/weryai_77735eb26d193896a250272bb58922eb.mp4',
    enhancedVisualRealism:
        'https://static.weryai.com/growth/video/weryai_55ce9cee74241cfdd11e273da70c7c3e.mp4',
} as const

definePageMeta({
    layout: 'website',
})

useMultilingualSeo({ pageType: 'wan_2_6' })

// BreadcrumbList SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: computed(() =>
                JSON.stringify({
                    '@context': 'https://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            name: 'Home',
                            item: 'https://www.weryai.com.cn/',
                        },
                        {
                            '@type': 'ListItem',
                            position: 2,
                            name: t('wan-2-6.generatorTitle'),
                            item: requestUrlHref,
                        },
                    ],
                })
            ),
        },
    ],
})

onMounted(() => {
    analyticsEvent('view_model_page_common', {
        model_name: 'wan_2_6',
        type: 'video',
    })
})

// Ref for generator section
const generatorSection = ref<HTMLElement | null>(null)

// Scroll to generator function
const scrollToGenerator = () => {
    if (generatorSection.value) {
        generatorSection.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }
}

// Structured Data for SEO
useHead({
    script: [
        {
            type: 'application/ld+json',
            innerHTML: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'SoftwareApplication',
                name: t('wan-2-6.generatorTitle'),
                description: t('wan-2-6.pageDescription'),
                applicationCategory: 'MultimediaApplication',
                operatingSystem: 'Web',
                offers: {
                    '@type': 'Offer',
                    price: '0',
                    priceCurrency: 'USD',
                },
                provider: {
                    '@type': 'Organization',
                    name: '微鲤AI',
                    url: 'https://www.weryai.com.cn',
                },
                featureList: [
                    t('wan-2-6.nativeAudioIntegrationTitle'),
                    t('wan-2-6.enhancedDynamicEffectsTitle'),
                    t('wan-2-6.preciseCameraControlTitle'),
                    t('wan-2-6.enhancedVisualRealismTitle'),
                ],
            }),
        },
    ],
})

// Tabs configuration
const tabs = computed(() => [
    { name: t('wan-2-6.tabImageToVideo'), external: false },
    { name: t('wan-2-6.tabTextToVideo'), external: false },
])

// Methods
const handleGenerate = (data: {
    type: 'image' | 'text'
    image?: File
    prompt?: string
}) => {
    // 组件内部已经处理了跳转逻辑
}

// HowTo Steps for SEO
const howToUseSteps = computed(() => [
    {
        title: t('wan-2-6.step1Title'),
        description: t('wan-2-6.step1Description'),
    },
    {
        title: t('wan-2-6.step2Title'),
        description: t('wan-2-6.step2Description'),
    },
    {
        title: t('wan-2-6.step3Title'),
        description: t('wan-2-6.step3Description'),
    },
])

// HowTo SEO - 注入结构化数据
useHowToSeo(
    computed(() => t('wan-2-6.howToUseTitle')),
    howToUseSteps
)

// YouTube Videos Data
const currentVideoIndex = ref(0)

const youtubeVideos = ref([
    {
        title: 'Wan AI Video 1',
        embedUrl: 'https://www.youtube.com/embed/gYcMhT-eZ_A',
    },
    {
        title: 'Wan AI Video 2',
        embedUrl: 'https://www.youtube.com/embed/Rq8gjZgV-jg',
    },
    {
        title: 'Wan AI Video 3',
        embedUrl: 'https://www.youtube.com/embed/Dp-CMOo-kOc',
    },
    {
        title: 'Wan AI Video 4',
        embedUrl: 'https://www.youtube.com/embed/CBInzq8etqw',
    },
    {
        title: 'Wan AI Video 5',
        embedUrl: 'https://www.youtube.com/embed/8zyq8Q0iyoE',
    },
    {
        title: 'Wan AI Video 6',
        embedUrl: 'https://www.youtube.com/embed/onA3mydjQkg',
    },
    {
        title: 'Wan AI Video 7',
        embedUrl: 'https://www.youtube.com/embed/QkDnlqVZiWY',
    },
])

// Video SEO - 注入 VideoObject 结构化数据
useVideoSeo(youtubeVideos.value)

// Twitter Tweet URLs Data
const twitterTweetUrls = [
    'https://twitter.com/Alibaba_Wan/status/2000930078037827972',
    'https://twitter.com/prjcthorizon90/status/2001030373430714704',
    'https://twitter.com/PFoggai/status/2000957859106877897',
    'https://twitter.com/yachimat_manga/status/2001057538570125818',
    'https://twitter.com/aiwarts/status/2000946233272361109',
    'https://twitter.com/aisearchio/status/2000822500150927837',
    'https://twitter.com/apalahstudio/status/2001077997013725470',
    'https://twitter.com/buhimi902/status/2001165940910948662',
    'https://twitter.com/ikyvenusious/status/2001115601734127905',
]

// Carousel Methods
const nextVideo = () => {
    // Maximum 3 visible videos, so max index is 2 (3rd video)
    const maxVisibleIndex = Math.min(2, youtubeVideos.value.length - 1)
    if (currentVideoIndex.value >= maxVisibleIndex) {
        currentVideoIndex.value = 0 // Back to first video
    } else {
        currentVideoIndex.value++
    }
}

const previousVideo = () => {
    if (currentVideoIndex.value <= 0) {
        // Back to last visible video position
        const maxVisibleIndex = Math.min(2, youtubeVideos.value.length - 1)
        currentVideoIndex.value = maxVisibleIndex
    } else {
        currentVideoIndex.value--
    }
}

// FAQ Data
const faqData = computed(() => [
    {
        question: t('wan-2-6.faq1Question'),
        answer: t('wan-2-6.faq1Answer'),
    },
    {
        question: t('wan-2-6.faq2Question'),
        answer: t('wan-2-6.faq2Answer'),
    },
    {
        question: t('wan-2-6.faq3Question'),
        answer: t('wan-2-6.faq3Answer'),
    },
    {
        question: t('wan-2-6.faq4Question'),
        answer: t('wan-2-6.faq4Answer'),
    },
    {
        question: t('wan-2-6.faq5Question'),
        answer: t('wan-2-6.faq5Answer'),
    },
    {
        question: t('wan-2-6.faq6Question'),
        answer: t('wan-2-6.faq6Answer'),
    },
])

// FAQ Interactive Functions
const activeFaqIndex = ref<number | null>(null)

const toggleFaq = (index: number) => {
    if (activeFaqIndex.value === index) {
        activeFaqIndex.value = null
    } else {
        activeFaqIndex.value = index
    }
}

// Video 交互功能
</script>

<style lang="less" scoped>
@import '~/assets/css/howToUse.css';

.highlight-red {
    color: #ef4444;
    font-weight: 600;
}
</style>
